import { Empty, Tag } from 'antd';
import { AlertOutlined } from '@ant-design/icons';
import type {
  DecisionRecordView,
  PriorDispositionRef,
  RunSummary,
  WhyItFired as WhyItFiredData,
} from '@/api/types';
import { StatusPill } from '@/components/StatusPill';
import { formatRelative } from '@/lib/time';
import { MemoryBadge } from './badges';
import { erasedOr } from '@/lib/erased';

/** Why-it-fired — always visible in the triage drawer (F-IS-03: no extra click). */
export function WhyItFiredPanel({ why }: { why: WhyItFiredData }) {
  return (
    <div className="ol-why">
      <div className="ol-section" style={{ marginBottom: 6, color: 'var(--ol-attention)' }}>
        <AlertOutlined aria-hidden /> Why it fired
      </div>
      {why.summary && <div style={{ fontSize: 12.5, lineHeight: 1.5 }}>{why.summary}</div>}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: why.summary ? 8 : 0 }}>
        {why.signalKind && (
          <Tag bordered={false} title="Signal kind">
            {why.signalKind}
          </Tag>
        )}
        {why.ruleId && (
          <Tag bordered={false} className="ol-mono" title="Rule id">
            {why.ruleId}
          </Tag>
        )}
        {why.fingerprint && (
          <Tag bordered={false} className="ol-mono" title="Signal fingerprint">
            fp: {why.fingerprint}
          </Tag>
        )}
      </div>
      {why.factors && why.factors.length > 0 && (
        <div style={{ marginTop: 8 }}>
          {why.factors.map((f, i) => (
            <div className="ol-why__factor" key={`${f.label}-${i}`}>
              <span style={{ color: 'var(--ol-text-secondary)' }}>{f.label}</span>
              <span style={{ fontWeight: 600 }}>{erasedOr(f.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Run summary card (HUMAN_TASK context). */
export function RunSummaryPanel({ run }: { run: RunSummary }) {
  return (
    <div className="ol-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 12.5 }}>{run.flowId ?? 'Run'}</div>
          <div className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)', wordBreak: 'break-all' }}>
            {run.runId}
            {run.flowVersion != null ? ` · v${run.flowVersion}` : ''}
          </div>
        </div>
        {run.outcome && <StatusPill status={run.outcome} />}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)', marginTop: 6 }}>
        {run.startedAt ? `started ${formatRelative(run.startedAt)}` : ''}
        {run.completedAt ? ` · completed ${formatRelative(run.completedAt)}` : ''}
      </div>
      {run.failure && (
        <div style={{ fontSize: 12, marginTop: 5, color: 'var(--ol-negative)' }}>{run.failure}</div>
      )}
    </div>
  );
}

/** Decision-ledger records for the item's run/case (chained-hash; PII-free). */
export function DecisionsPanel({ decisions }: { decisions: DecisionRecordView[] }) {
  if (!decisions || decisions.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No decisions recorded." />;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {decisions.map((d, i) => (
        <div className="ol-card" key={d.seq ?? i} style={{ padding: '9px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, fontSize: 12.5 }}>
              {d.policyVerdict ?? 'Decision'}
              {d.nodeId && (
                <span className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)', marginLeft: 6 }}>
                  {d.nodeId}
                </span>
              )}
            </span>
            <span className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
              #{d.seq}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ol-text-tertiary)', marginTop: 2 }}>
            {formatRelative(d.occurredAt)}
            {d.confidence != null ? ` · confidence ${d.confidence.toFixed(2)}` : ''}
          </div>
          <div style={{ marginTop: 5, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {d.pathType && (
              <Tag bordered={false} style={{ fontSize: 10 }}>
                {d.pathType}
              </Tag>
            )}
            {d.fallbackVerdict && (
              <Tag bordered={false} color="warning" style={{ fontSize: 10 }}>
                fallback: {d.fallbackVerdict}
              </Tag>
            )}
            {d.modelId && (
              <Tag bordered={false} className="ol-mono" style={{ fontSize: 10 }}>
                {d.modelId}
              </Tag>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The full prior-disposition memory list (drawer). */
export function PriorDispositionsPanel({ priors }: { priors: PriorDispositionRef[] }) {
  if (!priors || priors.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {priors.map((p, i) => (
        <div key={`${p.at}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <MemoryBadge prior={p} />
          {p.note && <span style={{ fontSize: 12, color: 'var(--ol-text-secondary)' }}>{erasedOr(p.note)}</span>}
        </div>
      ))}
    </div>
  );
}
